import { useEffect, useState } from 'react';
import { getSessions } from '../services/calendrierService';
import { formatDate } from '../pages/devisUtils';
import SelectRecherche from './SelectRecherche';

/**
 * Choix de la session INTER d'une ligne de devis.
 *
 * C'est sur cette session que le devis, une fois signé, prend ses places
 * (`devis_lignes.calendrier_id`, réservation côté serveur). Une ligne INTRA
 * n'a pas de session : sa date se négocie au contrat, le composant ne rend
 * rien. Sans choix, la date reste « à définir » et la planification du
 * contrat, depuis le calendrier, pourra encore rattacher la ligne.
 *
 * Une session complète reste proposée, signalée : le calendrier peut
 * dépasser sa capacité, et c'est à l'humain d'en juger.
 */

export const estInter = (format) => /^inter/.test(String(format || ''));

/** Les sessions à venir du calendrier, chargées une fois tant que `actif`. */
export function useSessionsCalendrier(actif = true) {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!actif) return undefined;
    let vivant = true;
    getSessions({ periode: 'a_venir' })
      .then((res) => {
        if (vivant) setSessions(res.data || []);
      })
      .catch(() => {
        // Sans calendrier, la ligne se fait sans session : rien à bloquer.
      });
    return () => {
      vivant = false;
    };
  }, [actif]);

  return sessions;
}

/** « 12 mars → 13 mars 2026 · Module 1 · Paris – présentiel · 5 places ». */
export function libelleSession(s) {
  const periode =
    String(s.date_debut) === String(s.date_fin)
      ? formatDate(s.date_debut)
      : `${formatDate(s.date_debut)} → ${formatDate(s.date_fin)}`;
  const restantes = Number(s.places_restantes);
  const places = Number.isFinite(restantes)
    ? restantes <= 0
      ? 'complet'
      : `${restantes} place${restantes > 1 ? 's' : ''}`
    : null;
  return [periode, s.module, s.lieux, places].filter(Boolean).join(' · ');
}

function SelectSessionCalendrier({
  sessions,
  formationId,
  format,
  value,
  onChange,
  disabled = false,
  libelleValeur = null,
}) {
  if (!estInter(format)) return null;

  const valeur = value === null || value === undefined ? '' : String(value);
  const proposees = sessions.filter(
    (s) => String(s.formation_id) === String(formationId) && s.statut !== 'annulee'
  );
  // La session déjà retenue peut être passée, ou d'une autre formation
  // après un changement : on la garde visible plutôt que de la perdre.
  const connue = proposees.some((s) => String(s.calendrier_id) === valeur);

  return (
    <label className="devis-form-field devis-form-field-session">
      <span>Session du calendrier</span>
      <SelectRecherche
        value={valeur}
        onChange={onChange}
        disabled={disabled || !formationId}
        title={
          !formationId
            ? 'Choisissez d’abord la formation'
            : 'Le devis signé réservera ses places sur cette session'
        }
      >
        <option value="">— Date à définir —</option>
        {valeur && !connue && (
          <option value={valeur}>{libelleValeur || `Session n° ${valeur}`}</option>
        )}
        {proposees.map((s) => (
          <option key={s.calendrier_id} value={s.calendrier_id}>
            {libelleSession(s)}
          </option>
        ))}
      </SelectRecherche>
    </label>
  );
}

export default SelectSessionCalendrier;
